import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout, Box, Spinner } from '@amzn/awsui-components-core';
import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import { McpConfig } from 'src/components/context/AppContextModels';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { appSyncOperationName } from 'src/constants/AppConstants';
import { listMcpConfigs, getMcpConfig } from 'src/graphql/queries';
import { logger } from 'src/logger';
import McpLandingPage from './components/McpLandingPage';
import McpServerDetailPage from './components/McpServerDetailPage';

export const McpShowcasePage: React.FC = () => {
  const { serverId: urlServerId } = useParams<{ serverId?: string }>();
  const navigate = useNavigate();
  const [servers, setServers] = useState<McpConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedServerId, setSelectedServerId] = useState<string | null>(urlServerId || null);
  const [selectedServer, setSelectedServer] = useState<McpConfig | null>(null);

  // Sync selectedServerId with URL param (e.g., browser back/forward)
  useEffect(() => {
    setSelectedServerId(urlServerId || null);
  }, [urlServerId]);

  useEffect(() => {
    const fetchServers = async () => {
      try {
        const data = await fetchGraphQLRequest<{ listMcpConfigs: McpConfig[] }>({
          query: listMcpConfigs,
          variables: {},
          requestName: 'listMcpConfigs',
          requestType: appSyncOperationName.QUERY
        });
        const items: McpConfig[] = data.listMcpConfigs || [];
        setServers(items);
      } catch (error) {
        logger.error('Failed to fetch MCP configs.', { error });
        setServers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServers();
  }, []);

  useEffect(() => {
    if (!selectedServerId) {
      setSelectedServer(null);
      return;
    }
    if (isLoading) return;
    const cached = servers.find((s) => s.id === selectedServerId);
    if (cached) {
      if (selectedServer?.id !== cached.id) {
        setSelectedServer(cached);
      }
    } else {
      // Fetch individually only if truly not in the loaded list
      const fetchSingle = async () => {
        try {
          const data = await fetchGraphQLRequest<{ getMcpConfig: McpConfig }>({
            query: getMcpConfig,
            variables: { id: selectedServerId },
            requestName: 'getMcpConfig',
            requestType: appSyncOperationName.QUERY
          });
          setSelectedServer(data.getMcpConfig || null);
        } catch (error) {
          logger.error('Failed to fetch MCP config.', { error, mcpId: selectedServerId });
          setSelectedServer(null);
        }
      };
      fetchSingle();
    }
  }, [selectedServerId, servers, isLoading]);

  const handleSelectServer = useCallback(
    (serverId: string) => {
      setSelectedServerId(serverId);
      navigate(`/mcp/${serverId}`, { replace: true });
    },
    [navigate]
  );

  const handleBack = useCallback(() => {
    setSelectedServerId(null);
    navigate('/mcp', { replace: true });
  }, [navigate]);

  // Build side nav dynamically, grouped by category
  const categorized = servers.reduce<Record<string, McpConfig[]>>((acc, server) => {
    const cat = server.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(server);
    return acc;
  }, {});

  const navItems: SideNavigationProps.Item[] = [
    { type: 'link', text: 'All MCP Servers', href: '/mcp' },
    { type: 'divider' },
    ...Object.entries(categorized).map(([category, categoryServers]) => ({
      type: 'section' as const,
      text: category,
      items: categoryServers.map((server) => ({
        type: 'link' as const,
        text: server.name,
        href: `/mcp/${server.id}`
      }))
    }))
  ];

  const handleFollow = useCallback<NonNullable<SideNavigationProps['onFollow']>>(
    (event) => {
      event.preventDefault();
      const href = event.detail.href;
      if (href === '/mcp') {
        handleBack();
      } else {
        const id = href.replace('/mcp/', '');
        handleSelectServer(id);
      }
    },
    [handleBack, handleSelectServer]
  );

  if (isLoading) {
    return (
      <AppLayout
        toolsHide
        navigationHide
        content={
          <Box textAlign="center" padding="xxl">
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>
              Loading MCP servers...
            </Box>
          </Box>
        }
      />
    );
  }

  // Detail view
  if (selectedServer) {
    return (
      <AppLayout
        navigation={
          <SideNavigation
            activeHref={`/mcp/${selectedServer.id}`}
            header={{ text: 'MCP Servers', href: '/mcp' }}
            items={navItems}
            onFollow={handleFollow}
          />
        }
        toolsHide
        maxContentWidth={Number.MAX_VALUE}
        content={<McpServerDetailPage server={selectedServer} onBack={handleBack} />}
      />
    );
  }

  // Landing page
  return (
    <AppLayout
      navigation={<SideNavigation activeHref="/mcp" header={{ text: 'MCP Servers', href: '/mcp' }} items={navItems} onFollow={handleFollow} />}
      toolsHide
      maxContentWidth={Number.MAX_VALUE}
      content={<McpLandingPage servers={servers} onSelectServer={handleSelectServer} />}
    />
  );
};
